/**
 * Constantes de l'application
 * En production (ex. https://51.38.189.131/admin), les appels API doivent partir en HTTPS
 * pour éviter 405 : une redirection HTTP→HTTPS en 301 transforme un POST en GET côté client.
 */

function getApiBaseUrl() {
  const envUrl = process.env.REACT_APP_API_URL;
  let base = envUrl ? envUrl.trim() : null;
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    const isHttps = origin.startsWith('https://');
    if (!base && (process.env.NODE_ENV === 'production' || isHttps)) {
      base = `${origin}/api`;
    } else if (
      base &&
      isHttps &&
      base.startsWith('http://') &&
      !base.includes('localhost')
    ) {
      base = `${origin}/api`;
    }
  }
  return base || 'http://localhost:3001/api';
}

export const API_BASE_URL = getApiBaseUrl();

// En dev : backend sur 3001 pour les redirections OAuth (window.location.href ne passe pas par le proxy)
// En prod : REACT_APP_AUTH_URL ou origine courante
export const AUTH_BASE_URL =
  process.env.REACT_APP_AUTH_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001'
    : typeof window !== 'undefined'
      ? window.location.origin
      : '');

// Utiliser REACT_APP_WS_URL si disponible et non vide, sinon REACT_APP_SOCKET_URL, sinon window.location.origin en prod
function getSocketUrl() {
  const wsUrl = process.env.REACT_APP_WS_URL?.trim();
  const socketUrl = process.env.REACT_APP_SOCKET_URL?.trim();

  // Si une URL explicite est fournie, l'utiliser
  if (wsUrl) return wsUrl;
  if (socketUrl) return socketUrl;

  // Sinon, en dev utiliser localhost, en prod utiliser l'origine actuelle
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3001';
  }

  return typeof window !== 'undefined' ? window.location.origin : '';
}

export const SOCKET_URL = getSocketUrl();

// Log pour déboguer (uniquement en développement)
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 Configuration Socket.IO:', {
    REACT_APP_WS_URL: process.env.REACT_APP_WS_URL,
    REACT_APP_SOCKET_URL: process.env.REACT_APP_SOCKET_URL,
    SOCKET_URL,
    API_BASE_URL,
  });
}

/** Version de l'application (lue depuis VERSION ou REACT_APP_VERSION au build) */
export const APP_VERSION = process.env.REACT_APP_VERSION || '0.0.0';

/** Informations légales (mentions légales, RGPD) — surchargeables via variables d'environnement */
export const LEGAL = {
  COMPANY_NAME: process.env.REACT_APP_LEGAL_COMPANY_NAME || 'AzuDev',
  ADDRESS: process.env.REACT_APP_LEGAL_ADDRESS || '',
  SIRET: process.env.REACT_APP_LEGAL_SIRET || '84127300600020',
  CONTACT_EMAIL:
    process.env.REACT_APP_CONTACT_EMAIL || 'azumagl.contact@gmail.com',
};

export const ROUTES = {
  HOME: '/',
  PROFILE: '/profile',
  DASHBOARD: '/dashboard',
  STREAM_STATS: '/stats',
  MENTIONS_LEGALES: '/mentions-legales',
  SET_PASSWORD: '/set-password',
  FORGOT_PASSWORD: '/forgot-password',
  TRIAL_CONFIRM: '/trial-confirm',
  MAINTENANCE: '/maintenance',
  NOT_FOUND: '*',
};

/** Onglets reconnus pour la query `?tab=` sur le dashboard */
export const DASHBOARD_VALID_TABS = [
  'overview',
  'rewards',
  'spotify',
  'followed',
  'mentions',
  'followssubs',
  'streamstats',
  'birthdays',
  'clips',
  'overlays',
  'commands',
  'ai',
  'parameters',
];

/** Identifiants des fonctionnalités freemium (alignés sur backend/app/constants/premiumFeatures.js) */
export const PREMIUM_FEATURE_IDS = {
  REWARDS_MANAGEMENT: 'rewards_management',
  SPOTIFY_INTEGRATION: 'spotify_integration',
  FOLLOWED_CHANNELS: 'followed_channels',
  CHAT_MENTIONS: 'chat_mentions',
  FOLLOWS_SUBS: 'follows_subs',
  STREAM_STATS: 'stream_stats',
  BIRTHDAYS: 'birthdays',
  CLIPS: 'clips',
  OVERLAYS: 'overlays',
  BATTLE_VIEWERS: 'battle_viewers',
  PARAMETERS_PANEL: 'parameters_panel',
  AI_AUTO_INTERACTION: 'ai_auto_interaction',
  ADVANCED_COMMANDS: 'advanced_commands',
};

/** id DOM de la section offre Premium (Vue d'ensemble) — ancre #… dans l'URL */
export const DASHBOARD_PREMIUM_SECTION_ID = 'dashboard-premium-offer';

/**
 * Chemin vers l'onglet Vue d'ensemble + section souscription Premium.
 * @returns {string}
 */
export function getDashboardPremiumOfferPath() {
  return `${ROUTES.DASHBOARD}?tab=overview#${DASHBOARD_PREMIUM_SECTION_ID}`;
}

export const STORAGE_KEYS = {
  TWITCH_USER: 'twitchUser',
  TWITCH_TOKEN: 'twitchToken',
  SPOTIFY_USER: 'spotifyUser',
  SPOTIFY_TOKEN: 'spotifyToken',
  CLIENT_ID: 'clientId',
  SELECTED_REWARD_ID: 'selectedRewardId',
  SPOTIFY_LOGOUT_IN_PROGRESS: 'spotify_logout_in_progress',
  ACTIVE_TAB: 'activeTab',
  ACTIVE_PARAMS_TAB: 'activeParamsTab',
  /** État ouvert/fermé des groupes de la sidebar dashboard (objet JSON) */
  SIDEBAR_NAV_SECTIONS: 'sidebarNavSections',
};

export const SOCKET_EVENTS = {
  CONNECT: 'connect',
  CONNECTION: 'connection', // Pour compatibilité serveur
  DISCONNECT: 'disconnect',
  REDEEM: 'redeem',
  REWARD: 'reward',
  CHEER: 'cheer',
  CHAT_MESSAGE: 'chat-message',
  CHAT_MENTION: 'chat_mention',
  CHAN_ONLINE: 'stream.online',
  /** Émis par le backend quand le refresh Twitch échoue ; le client doit rediriger vers OAuth (connectTwitch). */
  TWITCH_REAUTH_REQUIRED: 'twitch:reauth_required',
};

export const SOCKET_NAMESPACES = {
  STREAM: '/stream',
  CHAT: '/chat',
  SOUNDS: '/sounds',
  FOLLOWED: '/followed',
  REWARDS: '/rewards',
  OVERLAYS: '/overlays',
};

export const TOAST_CONFIG = {
  position: 'top-right',
  autoClose: 3000,
  hideProgressBar: false,
  newestOnTop: false,
  closeOnClick: true,
  rtl: false,
  pauseOnFocusLoss: true,
  draggable: true,
  pauseOnHover: true,
  theme: 'light',
  style: { zIndex: 9999 },
};
